import { useEffect, useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import Layout from "../components/Layout";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useMonth, MONTH_NAMES } from "../contexts/MonthContext";
import { MagnifyingGlass, ArrowLeft, ClipboardText, Printer } from "@phosphor-icons/react";

const fmt = (n) => {
  const v = Number(n || 0);
  if (Number.isNaN(v)) return "0";
  return v.toLocaleString("en-IN", { maximumFractionDigits: 2 });
};

const esc = (s) => String(s ?? "").replace(/[&<>"']/g, (c) => (
  { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]
));

function buildPrintHtml(data, periodLabel, companyName) {
  const { part, schedule, rm, wip, wip_total, issues } = data;
  const issueBlocks = (issues || []).map((g) => {
    const rows = (g.items || []).map((it) => (
      `<tr><td>${esc(it.data_id || "—")}</td><td>${esc(it.date || "—")}</td><td class="num">${fmt(it.issued_qty)}</td></tr>`
    )).join("");
    return `
      <div class="op-block">
        <div class="op-head">
          <span class="op-name">${esc(g.operation_name)}</span>
          <span class="op-total">Total: <b>${fmt(g.total)}</b></span>
        </div>
        ${(g.items && g.items.length)
          ? `<table class="tbl"><thead><tr><th>Issue No</th><th>Issue Date</th><th class="num">Issue Quantity</th></tr></thead><tbody>${rows}</tbody></table>`
          : `<div class="empty">No issues in this period.</div>`}
      </div>`;
  }).join("");

  const wipRows = (wip || []).map((w) => (
    `<tr><td>${esc(w.operation_name)}</td><td class="num">${fmt(w.qty)}</td></tr>`
  )).join("");

  return `<!doctype html>
<html><head>
<meta charset="utf-8" />
<title>Work Order — ${esc(part.part_no || "")}</title>
<style>
  @page { size: A4 portrait; margin: 14mm 12mm; }
  * { box-sizing: border-box; }
  body { font-family: -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif; color:#0F172A; margin:0; font-size:11px; line-height:1.4; }
  .toolbar { display:flex; justify-content:flex-end; margin-bottom:10px; }
  .toolbar button { padding:6px 14px; background:#1D2951; color:#fff; border:0; border-radius:4px; cursor:pointer; font-size:12px; font-weight:600; }
  .header { display:flex; justify-content:space-between; align-items:flex-start; border-bottom:2px solid #1D2951; padding-bottom:10px; margin-bottom:14px; }
  .header h1 { margin:0; font-size:20px; color:#1D2951; letter-spacing:0.3px; }
  .header .sub { color:#64748B; font-size:10px; margin-top:2px; }
  .header .meta { text-align:right; font-size:10px; color:#475569; }
  .header .meta b { color:#1D2951; }
  .section { margin-bottom:14px; page-break-inside:avoid; }
  .section h2 { font-size:11px; text-transform:uppercase; letter-spacing:0.8px; color:#1B3A5C; border-bottom:1px solid #CBD5E1; padding-bottom:4px; margin:0 0 8px 0; }
  .grid { display:grid; gap:8px 14px; }
  .grid-3 { grid-template-columns: repeat(3, 1fr); }
  .grid-5 { grid-template-columns: repeat(5, 1fr); }
  .grid-6 { grid-template-columns: repeat(6, 1fr); }
  .grid-2 { grid-template-columns: repeat(2, 1fr); }
  .field { background:#F8FAFC; border:1px solid #E2E8F0; border-radius:3px; padding:6px 8px; }
  .field .lbl { font-size:9px; color:#64748B; text-transform:uppercase; letter-spacing:0.5px; }
  .field .val { font-size:13px; font-weight:600; color:#0F172A; margin-top:2px; word-break:break-all; }
  .field.mono .val { font-family: ui-monospace, "SF Mono", Menlo, monospace; }
  .field.accent .val { color:#1D2951; }
  .tbl { width:100%; border-collapse:collapse; font-size:10.5px; }
  .tbl th { background:#1D2951; color:#fff; padding:6px 8px; text-align:left; font-weight:600; }
  .tbl td { padding:5px 8px; border:1px solid #E2E8F0; }
  .tbl tr:nth-child(even) td { background:#F8FAFC; }
  .tbl .num, .tbl th.num { text-align:right; font-family: ui-monospace, "SF Mono", Menlo, monospace; }
  .tbl tfoot td { background:#1D2951 !important; color:#fff; font-weight:700; }
  .op-block { margin-bottom:10px; page-break-inside:avoid; }
  .op-head { display:flex; justify-content:space-between; align-items:baseline; margin-bottom:4px; }
  .op-name { font-size:11px; font-weight:700; color:#1D2951; }
  .op-total { font-size:10px; color:#475569; }
  .op-total b { color:#1D2951; font-family: ui-monospace, monospace; }
  .empty { font-size:10px; color:#94A3B8; font-style:italic; padding:6px 8px; border:1px dashed #CBD5E1; border-radius:3px; }
  .foot-note { color:#64748B; font-size:9px; margin-top:4px; font-style:italic; }
  .footer { margin-top:20px; padding-top:8px; border-top:1px solid #CBD5E1; display:flex; justify-content:space-between; font-size:9px; color:#94A3B8; }
  @media print { .toolbar { display:none !important; } }
</style>
</head>
<body>
  <div class="toolbar"><button onclick="window.print()">🖨 Print / Save as PDF</button></div>

  <div class="header">
    <div>
      <h1>WORK ORDER</h1>
      <div class="sub">Read-only summary · Period: <b>${esc(periodLabel)}</b></div>
    </div>
    <div class="meta">
      <div><b>${esc(companyName || "Manufacturing Tracker")}</b></div>
      <div>Generated: ${new Date().toLocaleString("en-IN")}</div>
    </div>
  </div>

  <div class="section">
    <h2>Part Details</h2>
    <div class="grid grid-3">
      <div class="field"><div class="lbl">Customer</div><div class="val">${esc(part.customer || "—")}</div></div>
      <div class="field"><div class="lbl">Part Name</div><div class="val">${esc(part.description || "—")}</div></div>
      <div class="field mono accent"><div class="lbl">Part No</div><div class="val">${esc(part.part_no || "—")}</div></div>
    </div>
  </div>

  <div class="section">
    <h2>Schedule — ${esc(periodLabel)}</h2>
    <div class="grid grid-6">
      <div class="field mono"><div class="lbl">Schedule Quantity</div><div class="val">${fmt(schedule.schedule_qty)}</div></div>
      <div class="field mono"><div class="lbl">Carry Forward</div><div class="val">${fmt(schedule.carry_forward)}</div></div>
      <div class="field mono"><div class="lbl">Prepond Schedule</div><div class="val">${fmt(schedule.prepond_schedule)}</div></div>
      <div class="field mono accent"><div class="lbl">Cumulative Schedule</div><div class="val">${fmt(schedule.cumulative_schedule)}</div></div>
      <div class="field mono"><div class="lbl">Dispatch Quantity</div><div class="val">${fmt(schedule.dispatch_qty)}</div></div>
      <div class="field mono accent"><div class="lbl">Balance Quantity</div><div class="val">${fmt(schedule.balance_qty)}</div></div>
    </div>
    <div class="foot-note">Carry Forward = Σ prior orders − Σ prior dispatches · Prepond Schedule = open qty for POs delivering after this month · Balance = Cumulative Schedule − Dispatch Quantity</div>
  </div>

  <div class="section">
    <h2>Raw Material</h2>
    <div class="grid grid-2">
      <div class="field mono"><div class="lbl">RM Code</div><div class="val">${esc(rm.rm_code || "—")}</div></div>
      <div class="field mono accent"><div class="lbl">RM Stock (Kg)</div><div class="val">${fmt(rm.rm_stock_kg)}</div></div>
    </div>
  </div>

  <div class="section">
    <h2>WIP Details — Process-wise</h2>
    ${(wip && wip.length)
      ? `<table class="tbl"><thead><tr><th>Process</th><th class="num">Quantity (Nos)</th></tr></thead>
         <tbody>${wipRows}</tbody>
         <tfoot><tr><td>Total WIP</td><td class="num">${fmt(wip_total)}</td></tr></tfoot></table>`
      : `<div class="empty">No processes configured.</div>`}
  </div>

  <div class="section">
    <h2>Process-wise Issues — ${esc(periodLabel)}</h2>
    ${issueBlocks || `<div class="empty">No issues recorded.</div>`}
  </div>

  <div class="footer">
    <span>Swami Software PT · Work Order</span>
    <span>Page 1</span>
  </div>
</body></html>`;
}

function PartsList() {
  const navigate = useNavigate();
  const [parts, setParts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/work-order/parts`);
        setParts(r.data || []);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return parts;
    return parts.filter(p =>
      (p.part_no || "").toLowerCase().includes(q) ||
      (p.description || "").toLowerCase().includes(q) ||
      (p.customer || "").toLowerCase().includes(q) ||
      (p.company_part_no || "").toLowerCase().includes(q)
    );
  }, [parts, search]);

  return (
    <Layout>
      <div className="px-4 py-6 max-w-6xl mx-auto" data-testid="work-order-list-page">
        <div className="flex items-center gap-3 mb-5">
          <div className="p-2 rounded-lg bg-[#1B3A5C] text-white">
            <ClipboardText size={20} weight="fill" />
          </div>
          <div>
            <h1 className="text-2xl font-black text-[#1D2951]">Work Order</h1>
            <p className="text-sm text-[#64748B]">Select a part to view its read-only work-order summary.</p>
          </div>
        </div>

        <Card className="p-4 mb-4">
          <div className="relative">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" />
            <Input
              data-testid="work-order-search"
              placeholder="Search by Customer / Part No / Description / Company Part No"
              className="pl-9 min-h-[44px]"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </Card>

        <Card className="overflow-hidden">
          {loading ? (
            <div className="p-10 text-center text-[#64748B]">Loading parts…</div>
          ) : filtered.length === 0 ? (
            <div className="p-10 text-center text-[#64748B]">No parts found.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#F1F5F9] text-[#1D2951]">
                  <tr>
                    <th className="px-4 py-3 text-left font-bold">Customer</th>
                    <th className="px-4 py-3 text-left font-bold">Part No</th>
                    <th className="px-4 py-3 text-left font-bold">Part Name</th>
                    <th className="px-4 py-3 text-left font-bold">Company Part No</th>
                    <th className="px-4 py-3 text-left font-bold">RM Code</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((p) => (
                    <tr
                      key={p.id}
                      onClick={() => navigate(`/work-order/${p.id}`)}
                      className="border-t border-[#E2E8F0] hover:bg-[#F8FAFC] cursor-pointer transition-colors"
                      data-testid={`work-order-row-${p.part_no}`}
                    >
                      <td className="px-4 py-3">{p.customer || "—"}</td>
                      <td className="px-4 py-3 font-mono font-semibold text-[#1D2951]">{p.part_no || "—"}</td>
                      <td className="px-4 py-3">{p.description || "—"}</td>
                      <td className="px-4 py-3 text-[#64748B]">{p.company_part_no || "—"}</td>
                      <td className="px-4 py-3 font-mono">{p.rm_code || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </Layout>
  );
}

function ReadOnlyField({ label, value, mono, accent, testid }) {
  return (
    <div>
      <Label className="text-xs font-semibold text-[#475569]">{label}</Label>
      <Input
        readOnly
        value={value}
        data-testid={testid}
        className={`min-h-[44px] cursor-not-allowed bg-[#F8FAFC] ${mono ? 'font-mono' : ''} ${accent ? 'font-bold text-[#1D2951]' : ''}`}
      />
    </div>
  );
}

function PartWorkOrder() {
  const { partId } = useParams();
  const navigate = useNavigate();
  const { period } = useMonth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true); setErr("");
      try {
        const r = await axios.get(`${API}/work-order/${partId}`);
        if (!cancelled) setData(r.data);
      } catch (e) {
        if (!cancelled) setErr(e.response?.data?.detail || "Failed to load work order");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [partId, period.month, period.year]);

  if (loading) {
    return <Layout><div className="p-10 text-center text-[#64748B]">Loading…</div></Layout>;
  }
  if (err) {
    return <Layout><div className="p-10 text-center text-red-600">{err}</div></Layout>;
  }
  if (!data) return null;

  const { part, schedule, rm, wip, wip_total, issues } = data;
  const periodLabel = `${MONTH_NAMES[period.month - 1]} ${period.year}`;

  const handlePrint = () => {
    const companyName = document.querySelector('[data-testid="company-name-header"]')?.textContent?.trim() || "";
    const html = buildPrintHtml(data, periodLabel, companyName);
    const w = window.open("", "_blank", "width=900,height=1100");
    if (!w) return;
    w.document.open();
    w.document.write(html);
    w.document.close();
  };

  return (
    <Layout>
      <div className="px-4 py-6 max-w-5xl mx-auto" data-testid="work-order-detail-page">
        <button
          onClick={() => navigate("/work-order")}
          className="flex items-center gap-2 text-sm text-[#1D2951] hover:text-[#D4A843] mb-4 font-semibold"
          data-testid="work-order-back"
        >
          <ArrowLeft size={16} /> Back to parts list
        </button>

        <div className="flex items-start justify-between mb-5">
          <div>
            <h1 className="text-2xl font-black text-[#1D2951]">Work Order</h1>
            <p className="text-sm text-[#64748B]">Read-only summary · Period: <span className="font-semibold text-[#1D2951]">{periodLabel}</span></p>
          </div>
          <Button
            onClick={handlePrint}
            className="bg-[#1D2951] hover:bg-[#2A3B73] text-white"
            data-testid="work-order-print-btn"
          >
            <Printer size={16} className="mr-2" /> Print / Save as PDF
          </Button>
        </div>

        {/* Identity */}
        <Card className="p-5 mb-4">
          <h2 className="text-sm font-bold uppercase tracking-wider text-[#1B3A5C] mb-3 border-b border-[#E2E8F0] pb-2">Part Details</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <ReadOnlyField label="Customer" value={part.customer || "—"} testid="wo-customer" />
            <ReadOnlyField label="Part Name" value={part.description || "—"} testid="wo-part-name" />
            <ReadOnlyField label="Part No" value={part.part_no || "—"} mono accent testid="wo-part-no" />
          </div>
        </Card>

        {/* Schedule block */}
        <Card className="p-5 mb-4">
          <h2 className="text-sm font-bold uppercase tracking-wider text-[#1B3A5C] mb-3 border-b border-[#E2E8F0] pb-2">Schedule — {periodLabel}</h2>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
            <ReadOnlyField label="Schedule Quantity" value={fmt(schedule.schedule_qty)} mono testid="wo-schedule-qty" />
            <ReadOnlyField label="Carry Forward" value={fmt(schedule.carry_forward)} mono testid="wo-carry-forward" />
            <ReadOnlyField label="Prepond Schedule" value={fmt(schedule.prepond_schedule)} mono testid="wo-prepond-schedule" />
            <ReadOnlyField label="Cumulative Schedule" value={fmt(schedule.cumulative_schedule)} mono accent testid="wo-cumulative-schedule" />
            <ReadOnlyField label="Dispatch Quantity" value={fmt(schedule.dispatch_qty)} mono testid="wo-dispatch-qty" />
            <ReadOnlyField label="Balance Quantity" value={fmt(schedule.balance_qty)} mono accent testid="wo-balance-qty" />
          </div>
          <p className="text-xs text-[#64748B] mt-3">
            Carry Forward = Σ prior-period orders − Σ prior-period dispatches.
            Prepond Schedule = Σ open qty (remaining) for POs with delivery date AFTER this month.
            Cumulative Schedule = Carry Forward + Schedule Quantity.
            Balance Quantity = Cumulative Schedule − Dispatch Quantity.
          </p>
        </Card>

        {/* RM block */}
        <Card className="p-5 mb-4">
          <h2 className="text-sm font-bold uppercase tracking-wider text-[#1B3A5C] mb-3 border-b border-[#E2E8F0] pb-2">Raw Material</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <ReadOnlyField label="RM Code" value={rm.rm_code || "—"} mono testid="wo-rm-code" />
            <ReadOnlyField label="RM Stock (Kg)" value={fmt(rm.rm_stock_kg)} mono accent testid="wo-rm-stock" />
          </div>
        </Card>

        {/* WIP block */}
        <Card className="p-5 mb-4">
          <h2 className="text-sm font-bold uppercase tracking-wider text-[#1B3A5C] mb-3 border-b border-[#E2E8F0] pb-2">WIP Details — Process-wise</h2>
          {wip.length === 0 ? (
            <div className="text-sm text-[#64748B]">No processes configured.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#F1F5F9] text-[#1D2951]">
                  <tr>
                    <th className="px-4 py-2 text-left font-bold">Process</th>
                    <th className="px-4 py-2 text-right font-bold">Quantity (Nos)</th>
                  </tr>
                </thead>
                <tbody>
                  {wip.map((w) => (
                    <tr key={w.operation} className="border-t border-[#E2E8F0]" data-testid={`wo-wip-${w.operation}`}>
                      <td className="px-4 py-2">{w.operation_name}</td>
                      <td className="px-4 py-2 text-right font-mono">{fmt(w.qty)}</td>
                    </tr>
                  ))}
                  <tr className="border-t-2 border-[#1B3A5C] bg-[#F1F5F9]" data-testid="wo-wip-total">
                    <td className="px-4 py-2 font-bold text-[#1B3A5C]">Total WIP</td>
                    <td className="px-4 py-2 text-right font-mono font-black text-[#1B3A5C]">{fmt(wip_total)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
          <p className="text-xs text-[#64748B] mt-3">
            Quantity per process = latest balanced_qty_produced (live stock at that stage).
            Dispatch is excluded from WIP.
          </p>
        </Card>

        {/* Process-wise Issues — Issue No / Date / Quantity per process */}
        <Card className="p-5 mb-4">
          <h2 className="text-sm font-bold uppercase tracking-wider text-[#1B3A5C] mb-3 border-b border-[#E2E8F0] pb-2">
            Process-wise Issues — {periodLabel}
          </h2>
          {(!issues || issues.length === 0) ? (
            <div className="text-sm text-[#64748B]">No issues recorded.</div>
          ) : (
            <div className="space-y-5">
              {issues.map((grp) => (
                <div key={grp.operation} data-testid={`wo-issues-${grp.operation}`}>
                  <div className="flex items-center justify-between mb-1">
                    <h3 className="text-sm font-bold text-[#1D2951]">{grp.operation_name}</h3>
                    {grp.items.length > 0 && (
                      <span className="text-xs text-[#64748B]">
                        Total: <span className="font-mono font-bold text-[#1D2951]">{fmt(grp.total)}</span>
                      </span>
                    )}
                  </div>
                  {grp.items.length === 0 ? (
                    <div className="text-xs text-[#94A3B8] italic px-2 py-2 border border-dashed border-[#E2E8F0] rounded">No issues in this period.</div>
                  ) : (
                    <div className="overflow-x-auto border border-[#E2E8F0] rounded">
                      <table className="w-full text-sm">
                        <thead className="bg-[#F1F5F9] text-[#1D2951]">
                          <tr>
                            <th className="px-3 py-2 text-left font-bold">Issue No</th>
                            <th className="px-3 py-2 text-left font-bold">Issue Date</th>
                            <th className="px-3 py-2 text-right font-bold">Issue Quantity</th>
                          </tr>
                        </thead>
                        <tbody>
                          {grp.items.map((it, idx) => (
                            <tr key={idx} className="border-t border-[#E2E8F0]">
                              <td className="px-3 py-2 font-mono">{it.data_id || "—"}</td>
                              <td className="px-3 py-2">{it.date || "—"}</td>
                              <td className="px-3 py-2 text-right font-mono">{fmt(it.issued_qty)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </Layout>
  );
}

export default function WorkOrder() {
  const { partId } = useParams();
  return partId ? <PartWorkOrder /> : <PartsList />;
}
